import { useMutation } from '@apollo/client';
import { AdminButton } from '@website/components/admin/AdminButton';
import {
  AddReplyToReviewDocument,
  ReplyToReviewInput,
  Review,
  TripsPageQuery,
} from '@website/types/graphqlOperations';
import { useCallback, useState } from 'react';
import { Form, FormControl, FormGroup, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

type ReplyToReviewFormProps = {
  review: Pick<Review, 'id' | 'reply'> & {
    user: TripsPageQuery['tripByURL']['reviews']['items'][number]['user'];
  };
  open: boolean;
  onClose: () => void;
};

export function ReplyToReviewForm({
  review,
  open,
  onClose,
}: ReplyToReviewFormProps) {
  const [addReplyMutation, { loading }] = useMutation(
    AddReplyToReviewDocument,
    {},
  );

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<ReplyToReviewInput>({
    mode: 'onBlur',
    defaultValues: {
      reviewID: review.id,
      reply: review.reply?.content,
    },
  });

  const onSubmit = useCallback(
    async (data: ReplyToReviewInput) => {
      await addReplyMutation({
        variables: {
          input: data,
        },
      });
      onClose();
    },
    [addReplyMutation, onClose],
  );

  return (
    <Modal show={open} onHide={() => onClose()}>
      <Modal.Header closeButton>
        <Modal.Title>
          Replying to {review.user?.givenName}'s review (Admin Only)
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <FormGroup controlId="formBasicEmail">
            <Form.Label>Reply</Form.Label>
            <FormControl
              as="textarea"
              placeholder="Reply to review..."
              name="note"
              required
              {...register('reply', { required: true })}
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <AdminButton disabled={!isValid} type="submit" variant="danger">
            {loading ? <>Replying</> : 'Reply'}
          </AdminButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

type Props = {
  review: Pick<Review, 'id' | 'reply'> & {
    user: TripsPageQuery['tripByURL']['reviews']['items'][number]['user'];
  };
};

export function ReplyToReview({ review }: Props) {
  const [show, setShow] = useState(false);

  return (
    <>
      <ReplyToReviewForm
        review={review}
        open={show}
        onClose={() => setShow(false)}
      />
      <AdminButton
        className="mx-1"
        variant="danger"
        onClick={() => {
          setShow(true);
        }}
      >
        Reply
      </AdminButton>
    </>
  );
}
